// Seeded wind gusts. Produces a smooth, deterministic heat perturbation over
// time by summing a few sine waves with seeded phases/frequencies. Same seed +
// amplitude + time → same perturbation, which keeps smoke runs reproducible.

import type { Rng } from './rng';

export interface Wind {
  /** Heat perturbation (heat units) at a given elapsed time in seconds. */
  at(timeSeconds: number): number;
}

interface Component {
  freq: number; // radians per second
  phase: number; // radians
  weight: number; // relative contribution
}

const COMPONENT_COUNT = 3;

/**
 * Build a wind generator. `amplitude` is the peak perturbation in heat units;
 * amplitude 0 yields a generator that always returns 0. The component
 * frequencies/phases are drawn once from the seeded RNG so the gust pattern is
 * fully determined by the seed.
 */
export function makeWind(rng: Rng, amplitude: number): Wind {
  if (amplitude <= 0) {
    return { at: () => 0 };
  }

  const components: Component[] = [];
  let totalWeight = 0;
  for (let i = 0; i < COMPONENT_COUNT; i++) {
    const freq = rng.range(0.15, 0.9) * Math.PI * 2; // ~0.15–0.9 Hz
    const phase = rng.range(0, Math.PI * 2);
    const weight = rng.range(0.5, 1);
    components.push({ freq, phase, weight });
    totalWeight += weight;
  }

  return {
    at: (timeSeconds: number): number => {
      let sum = 0;
      for (const c of components) {
        sum += c.weight * Math.sin(c.freq * timeSeconds + c.phase);
      }
      return (sum / totalWeight) * amplitude;
    },
  };
}
